import { useMemo, useState } from 'react';
import { Controller, useFieldArray, type Control } from 'react-hook-form';
import type { ColumnDef } from '@tanstack/react-table';
import { Trash2Icon } from 'lucide-react';
import { Button, Input, Label, QueryErrorAlert, Table, TextLink } from '@/shared/components';
import { isApiError } from '@/services/api';
import { useItemsQuery } from '../../hooks/useItemsQuery';
import { ItemCombobox } from '../ItemCombobox';
import { itemPickerParams } from '../../utils/itemPickerParams';
import type { ServiceFormInput, ServiceFormOutput, ServiceInclusionInput } from '../../schemas/service.schema';

interface ServiceInclusionsFieldProps {
    control: Control<ServiceFormInput, unknown, ServiceFormOutput>;
    fieldId: (field: string) => string;
    disabled: boolean;
    isTotalMode: boolean;
    // Names for stored rows, keyed by item id.
    knownItemNames?: Record<string, string>;
}

interface InclusionRow extends ServiceInclusionInput {
    id: string;
    index: number;
}

interface InclusionsTableMeta {
    nameOf: (itemId: string) => string;
    hintIdFor: (index: number, field: string) => string;
    disabled: boolean;
    remove: (index: number) => void;
}

const ZERO_PRICE = '0.00';

const validateDraftQuantity = (raw: string) => {
    const quantity = Number(raw);
    if (raw.trim() === '' || Number.isNaN(quantity) || !Number.isInteger(quantity)) {
        return 'Quantity must be a whole number.';
    }
    return quantity < 1 ? 'Quantity must be 1 or greater.' : null;
};

export function ServiceInclusionsField({ control, fieldId, disabled, isTotalMode, knownItemNames }: ServiceInclusionsFieldProps) {
    const { fields, append, remove } = useFieldArray({ control, name: 'items' });
    const includedIds = [...new Set(fields.map((row) => row.item_id))];
    const {
        isError: isCatalogueError,
        isFetching: isCatalogueFetching,
        error: catalogueError,
        refetch: refetchCatalogue,
    } = useItemsQuery(itemPickerParams('', includedIds.length), { suppressForbiddenToast: true });
    // The catalogue needs smr.item.browse, which service editing does not imply: a 403 is
    // a permission boundary, not a transient failure, so it gets copy of its own and no retry.
    const isCatalogueForbidden = isApiError(catalogueError) && catalogueError.status === 403;

    const [draftItemId, setDraftItemId] = useState('');
    const [draftQuantity, setDraftQuantity] = useState('1');
    const [draftPrice, setDraftPrice] = useState(ZERO_PRICE);
    const [draftItemError, setDraftItemError] = useState<string | null>(null);
    const [draftQuantityError, setDraftQuantityError] = useState<string | null>(null);

    const [pickedNames, setPickedNames] = useState<Record<string, string>>({});

    const nameOf = (itemId: string) => pickedNames[itemId] ?? knownItemNames?.[itemId] ?? itemId;
    const hintIdFor = (index: number, field: string) => fieldId(`items-${index}-${field}-hint`);

    const rows: InclusionRow[] = fields.map((row, index) => ({ ...row, index }));

    const addRow = () => {
        const itemError = draftItemId ? null : 'Select an item.';
        const quantityError = validateDraftQuantity(draftQuantity);
        setDraftItemError(itemError);
        setDraftQuantityError(quantityError);
        if (itemError || quantityError) return;

        append({
            item_id: draftItemId,
            unit_quantity: Number(draftQuantity),
            unit_price_net: isTotalMode ? ZERO_PRICE : draftPrice,
        });
        setDraftItemId('');
        setDraftQuantity('1');
        setDraftPrice(ZERO_PRICE);
    };

    const columns = useMemo<ColumnDef<InclusionRow>[]>(() => {
        const defs: ColumnDef<InclusionRow>[] = [
            {
                id: 'item',
                header: 'Item',
                cell: (info) => {
                    const { nameOf, hintIdFor } = info.table.options.meta as InclusionsTableMeta;
                    const { index, item_id } = info.row.original;
                    return (
                        <Controller
                            name={`items.${index}.item_id`}
                            control={control}
                            render={({ fieldState: { error } }) => (
                                <>
                                    <span className="text-sm">{nameOf(item_id)}</span>
                                    {error?.message && (
                                        <p role="alert" id={hintIdFor(index, 'item_id')} className="mt-1.5 text-xs text-mp-error">
                                            {error.message}
                                        </p>
                                    )}
                                </>
                            )}
                        />
                    );
                },
            },
            {
                id: 'quantity',
                header: 'Qty',
                cell: (info) => {
                    const { nameOf, hintIdFor, disabled } = info.table.options.meta as InclusionsTableMeta;
                    const { index, item_id } = info.row.original;
                    const hintId = hintIdFor(index, 'unit_quantity');
                    return (
                        <Controller
                            name={`items.${index}.unit_quantity`}
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <Input
                                    type="number"
                                    min="1"
                                    aria-label={`Quantity for ${nameOf(item_id)}`}
                                    value={String(field.value ?? '')}
                                    onChange={(e) => field.onChange(e.target.value)}
                                    onBlur={field.onBlur}
                                    disabled={disabled}
                                    error={!!error}
                                    hint={error?.message}
                                    hintId={hintId}
                                    aria-invalid={!!error}
                                    aria-describedby={error ? hintId : undefined}
                                    className="w-24"
                                />
                            )}
                        />
                    );
                },
            },
        ];

        if (!isTotalMode) {
            defs.push({
                id: 'price',
                header: 'Unit Net (£)',
                cell: (info) => {
                    const { nameOf, hintIdFor, disabled } = info.table.options.meta as InclusionsTableMeta;
                    const { index, item_id } = info.row.original;
                    const hintId = hintIdFor(index, 'unit_price_net');
                    return (
                        <Controller
                            name={`items.${index}.unit_price_net`}
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <Input
                                    type="number"
                                    min="0"
                                    step={0.0001}
                                    aria-label={`Unit net price for ${nameOf(item_id)}`}
                                    value={field.value ?? ''}
                                    onChange={(e) => field.onChange(e.target.value)}
                                    onBlur={field.onBlur}
                                    disabled={disabled}
                                    error={!!error}
                                    hint={error?.message}
                                    hintId={hintId}
                                    aria-invalid={!!error}
                                    aria-describedby={error ? hintId : undefined}
                                    className="w-32"
                                />
                            )}
                        />
                    );
                },
            });
        }

        defs.push({
            id: 'actions',
            header: '',
            cell: (info) => {
                const { nameOf, disabled, remove } = info.table.options.meta as InclusionsTableMeta;
                return (
                    <TextLink
                        ariaLabel={`Remove ${nameOf(info.row.original.item_id)}`}
                        onClick={() => remove(info.row.original.index)}
                        disabled={disabled}
                        startIcon={<Trash2Icon size={16} />}
                        color="error" className="px-3 py-2"
                    />
                );
            },
        });

        return defs;
    }, [control, isTotalMode]);

    return (
        <div className="md:col-span-2 space-y-3">
            <Label id={fieldId('items_label')}>Inclusions</Label>
            <p className="text-sm text-mp-muted">
                {isTotalMode
                    ? 'Descriptive lines only. Total-cost services price the whole visit, so items carry no price of their own.'
                    : 'Parts and labour lines that make up this service. Cost is their sum.'}
            </p>

            <Table<InclusionRow>
                data={rows}
                columns={columns}
                enableSorting={false}
                getRowId={(row) => row.id}
                emptyMessage="No inclusions yet."
                meta={{ nameOf, hintIdFor, disabled, remove }}
            />

            {isCatalogueError ? (
                <QueryErrorAlert
                    message={isCatalogueForbidden
                        ? 'You do not have catalogue access, so inclusions cannot be added here.'
                        : isApiError(catalogueError) ? catalogueError.message : 'Failed to load the item catalogue.'}
                    onRetry={isCatalogueForbidden ? undefined : () => void refetchCatalogue()}
                    isRetrying={isCatalogueFetching}
                />
            ) : (
                <div className={`grid grid-cols-1 gap-3 items-end ${isTotalMode ? 'md:grid-cols-3' : 'md:grid-cols-4'}`}>
                    <div>
                        <Label htmlFor={fieldId('inclusion_item')}>Item</Label>
                        <ItemCombobox
                            id={fieldId('inclusion_item')}
                            value={draftItemId}
                            onChange={(id, item) => {
                                setDraftItemId(id);
                                setDraftItemError(null);
                                if (item) setPickedNames((names) => ({ ...names, [item.id]: item.name }));
                            }}
                            excludeIds={includedIds}
                            suppressForbiddenToast
                            disabled={disabled}
                            aria-invalid={!!draftItemError}
                            aria-describedby={draftItemError ? fieldId('inclusion_item-hint') : undefined}
                        />
                        {draftItemError && (
                            <p role="alert" id={fieldId('inclusion_item-hint')} className="mt-1 text-xs text-mp-error">
                                {draftItemError}
                            </p>
                        )}
                    </div>
                    <div>
                        <Label htmlFor={fieldId('inclusion_quantity')}>Qty</Label>
                        <Input
                            id={fieldId('inclusion_quantity')}
                            type="number"
                            min="1"
                            value={draftQuantity}
                            onChange={(e) => { setDraftQuantity(e.target.value); setDraftQuantityError(null); }}
                            disabled={disabled}
                            error={!!draftQuantityError}
                            hint={draftQuantityError ?? undefined}
                            hintId={fieldId('inclusion_quantity-hint')}
                            aria-invalid={!!draftQuantityError}
                            aria-describedby={draftQuantityError ? fieldId('inclusion_quantity-hint') : undefined}
                        />
                    </div>
                    {!isTotalMode && (
                        <div>
                            <Label htmlFor={fieldId('inclusion_price')}>Unit Net (£)</Label>
                            <Input
                                id={fieldId('inclusion_price')}
                                type="number"
                                min="0"
                                step={0.0001}
                                value={draftPrice}
                                onChange={(e) => setDraftPrice(e.target.value)}
                                disabled={disabled}
                            />
                        </div>
                    )}
                    <Button type="button" size="sm" variant="outline" onClick={addRow} disabled={disabled}>
                        Add Item
                    </Button>
                </div>
            )}
        </div>
    );
}
